import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Text } from '@vnetwork/datacentral-ui/primitives'
import { Eye, EyeOff } from 'lucide-react'

export type PrivacyFieldType = 'uuid' | 'email' | 'phone' | 'custom'

interface PrivacyFieldProps {
    value: string
    label?: string
    type?: PrivacyFieldType
    maskPattern?: string
    className?: string
    toggleable?: boolean
    onReveal?: (value: string) => void | Promise<void>
    onHide?: (value: string) => void | Promise<void>
    defaultVisible?: boolean
}

/** Mask value based on field type */
export const getMaskedValue = (
    type: PrivacyFieldType,
    originalValue: string,
    maskPattern?: string
): string => {
    if (maskPattern) return maskPattern

    switch (type) {
        case 'uuid':
            return '••••••••-••••-••••-••••-••••••••••••'
        case 'email': {
            const [username, domain] = originalValue.split('@')
            if (!domain) return '••••••••••@••••••••••'
            const visiblePart = username.substring(0, 3)
            const maskedPart = '•'.repeat(Math.max(username.length - 3, 0))
            return `${visiblePart}${maskedPart}@${domain}`
        }
        case 'phone': {
            const visiblePart = originalValue.substring(0, 3)
            const maskedPart = originalValue.substring(3).replace(/\d/g, '•')
            return visiblePart + maskedPart
        }
        default:
            return '••••••••••••••••••••'
    }
}

const PrivacyField: React.FC<PrivacyFieldProps> = ({
    value,
    label,
    type = 'custom',
    maskPattern,
    className = '',
    toggleable = false,
    onReveal,
    onHide,
    defaultVisible = false,
}) => {
    const { t } = useTranslation()
    const [isVisible, setIsVisible] = useState(defaultVisible)

    const handleReveal = async () => {
        if (onReveal) await onReveal(value)
        setIsVisible(true)
    }

    const handleHide = async () => {
        if (onHide) await onHide(value)
        setIsVisible(false)
    }

    const maskedValue = getMaskedValue(type, value, maskPattern)

    return (
        <div className={`flex flex-col ${className} gap-1 min-w-0`}>
            {label && (
                <Text size="xs" weight="medium" tone="muted">
                    {label}
                </Text>
            )}
            <div
                className="relative max-w-full overflow-hidden"
                onClick={isVisible && toggleable ? handleHide : undefined}
                style={
                    isVisible && toggleable ? { cursor: 'pointer' } : undefined
                }
                title={
                    isVisible && toggleable
                        ? t('click-to-hide', 'Hide')
                        : undefined
                }
            >
                <span className="text-xs text-muted-foreground font-mono px-1.5 py-1 bg-subtle rounded-sm border border-border-subtle flex items-center leading-tight truncate">
                    {isVisible ? value : maskedValue}
                </span>
                {!isVisible && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleReveal}
                        startIcon={<Eye className="w-3 h-3" />}
                        // Opaque: this covers the masked value, and at 80%
                        // the mask showed through and collided with the label.
                        className="absolute inset-0 h-full w-full rounded-sm bg-muted text-muted-foreground"
                    >
                        {t('click-to-reveal')}
                    </Button>
                )}
                {isVisible && toggleable && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-sm cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
                        <span className="text-muted-foreground flex items-center">
                            <EyeOff className="w-3 h-3" />
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PrivacyField
